import {
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  BORDER_RADIUS,
  COLORS,
  SHADOWS,
  SIZES,
  SPACING,
  TYPOGRAPHY,
} from "./constants";
import type { ManagerReviewItem } from "./types";

interface ReviewItemProps extends ManagerReviewItem {
  onDelete?: (id: string) => void;
  onDoItNow?: (id: string) => void;
}

export default function ReviewItem({
  id,
  iconUrl,
  heading,
  description,
  count,
  onDelete,
  onDoItNow,
}: ReviewItemProps) {
  // Format count as 2-digit string (01, 02, etc.)
  const formattedCount = String(count).padStart(2, "0");

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        padding: `${SPACING.xl} ${SPACING.xxl}`,
        alignItems: "flex-start",
        gap: SPACING.lg,
        alignSelf: "stretch",
        mb: 1.5,
        backgroundColor: COLORS.white,
        borderRadius: BORDER_RADIUS.lg,
        boxShadow: SHADOWS.xs,
      }}
    >
      {/* Left: Icon and content */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: SPACING.lg,
          flex: 1,
        }}
      >
        {/* Icon circle */}
        <Box
          sx={{
            width: SIZES.icon.container,
            height: SIZES.icon.container,
            borderRadius: BORDER_RADIUS.circle,
            backgroundColor: COLORS.background.iconCircle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {iconUrl && (
            <Box
              component="img"
              src={iconUrl}
              alt=""
              sx={{
                width: SIZES.icon.medium,
                height: SIZES.icon.medium,
              }}
            />
          )}
        </Box>

        {/* Text content */}
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.body.large,
              color: COLORS.text.primary,
              mb: 0.5,
            }}
          >
            {heading}
          </Typography>
          <Typography
            sx={{
              ...TYPOGRAPHY.body.medium,
              color: COLORS.text.secondary,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>

      {/* Right: Count, DO IT NOW button, and delete */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: SPACING.lg }}>
        {/* Count display */}
        <Typography
          sx={{
            ...TYPOGRAPHY.count.large,
            color: COLORS.text.primary,
            minWidth: "60px",
            textAlign: "center",
          }}
        >
          {formattedCount}
        </Typography>

        {/* DO IT NOW button */}
        <Button
          variant="contained"
          onClick={() => onDoItNow?.(id)}
          endIcon={
            <Box
              component="img"
              src="/static/images/chevron-right-white.svg"
              alt=""
              sx={{
                width: SIZES.icon.small,
                height: SIZES.icon.small,
              }}
            />
          }
          sx={{
            ...TYPOGRAPHY.button.medium,
            backgroundColor: COLORS.primary,
            color: COLORS.white,
            padding: `${SPACING.sm} ${SPACING.lg}`,
            borderRadius: BORDER_RADIUS.md,
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: COLORS.primaryHover,
            },
          }}
        >
          DO IT NOW
        </Button>

        {/* Delete button */}
        <Tooltip title="Dismiss">
          <IconButton
            onClick={() => onDelete?.(id)}
            sx={{
              width: SIZES.button.icon,
              height: SIZES.button.icon,
              padding: 0,
              "&:hover": {
                backgroundColor: COLORS.errorHover,
              },
            }}
          >
            <Box
              component="img"
              src="/static/images/close-red.svg"
              alt="Delete"
              sx={{
                width: SIZES.icon.small,
                height: SIZES.icon.small,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}
